import {
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  Option,
  Select,
  Sheet,
  Stack,
  Tab,
  tabClasses,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from "@mui/joy";
import CatalogueHeader from "../Items/components/CatalogueHeader";
import CatalogueMetadata from "../../components/Catalogue/CatalogueMetadata";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { paymentFlowTypes } from "../../types/order";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { theme } from "../../components/Theme";
import { CatalogType, ICatalogInviteCreatePayload } from "../../types/catalog";
import useContainer from "./useContainer";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import CopyLinkButton from "../../components/CopyLinkButton";
import { SuccessModal } from "../../components/SuccessModal";
import { InventoryService } from "../../services/inventory.service";
import { TempAccountType } from "../../components/Inventory/CreateCatalogModal";
import { Add } from "@mui/icons-material";
import { AccountService } from "../../services/account.service";
import { getActiveBusiness } from "../../store/session";
import NotFound from "../NotFound/NotFound";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getInitials } from "../../utils/misc";
import { formatDate } from "../../utils/formatDate";
import { CatalogService } from "../../services/catalog.service";
import { TrackerService } from "../../services/tracker.service";
import { TRACKER_CONSTANTS } from "../../constants/tracker.events";
import toast from "react-hot-toast";
import showErrorToast, {
  ErrorToastConfig,
  showSuccessToast,
  SuccessToastConfig,
} from "../../components/Error";
import ConfirmRemoveInvitationModal from "./components/ConfirmRemoveInvitationModal";

interface RecipientType {
  email: string | null;
  phoneNumber: string | null;
  phoneNumberCountryCode: string | null;
  hasBusiness: boolean;
  selectedBusinessId: string | null;
  authStrategy?: string;
}

const ShareCatalogue = () => {
  const { t } = useTranslation();

  const { catalogId } = useParams();

  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<CatalogType>();
  const [selectedTab, setSelectedTab] = useState("share");

  const { fetchOneCatalog } = useContainer();

  /**
   * fetch catalog based on catalogId, assign to catalog state
   */
  useEffect(() => {
    if (catalogId) {
      (async () => {
        const catalogResponse = await fetchOneCatalog(catalogId);

        if (catalogResponse) {
          setCatalog(catalogResponse);
        }
      })();
    }
  }, [catalogId]);

  // memoize catalog's info, only recalculate if catalog changes
  const CATALOG_METADATA = useMemo(() => {
    return [
      // Payment Flow Type
      catalog?.paymentFlowType && {
        title: t("catalog.catalogTypeOfPayment"),
        value:
          paymentFlowTypes.find(
            (flow) => flow.value === catalog.paymentFlowType
          )?.label || "",
      },

      // Tax ID
      catalog?.taxId && {
        title: t("catalog.catalogTax"),
        value: catalog.taxId.taxLabel as string,
      },

      // Catalog Period
      {
        title: t("catalog.catalogPeriod"),
        value: (
          <Typography
            level="text-xs-semibold"
            textColor={
              dayjs().isAfter(catalog?.periodEndDate)
                ? theme.palette.danger[400]
                : theme.palette.common.black
            }
          >
            :{" "}
            {catalog?.periodStartDate
              ? `${dayjs(catalog.periodStartDate).format(
                  "YYYY-MM-DD"
                )} ~ ${dayjs(catalog.periodEndDate).format("YYYY-MM-DD")}`
              : "-"}
          </Typography>
        ),
      },

      // Currency
      catalog?.currency && {
        title: t("catalog.catalogCurrency"),
        value: catalog.currency,
      },

      // Total Items
      catalog?.itemsCount && {
        title: t("catalog.catalogTotalItems"),
        value: `${catalog.itemsCount} Items`,
      },

      // Last Updated
      {
        title: t("catalog.catalogLastUpdated"),
        value: dayjs(catalog?.updatedAt).format("YYYY-MM-DD"),
      },
    ].filter(Boolean) as { title: string; value: string | ReactNode }[];
  }, [catalog]);

  /**
   * change active tab value state
   * @param _event
   * @param value
   */
  const handleTabChange = (
    _event: React.SyntheticEvent | null,
    value: string | number | null
  ) => {
    if (typeof value === "string") {
      setSelectedTab(value);
    }
  };

  if (!catalogId) {
    return <NotFound />;
  }

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        width: { xs: "92.28vw", lg: "100%" },
        display: "flex",
        flexDirection: "column",
        gap: { md: "24px" },
      }}
    >
      {/* back button lg */}
      <Box
        sx={{
          mt: "24px",
          display: { xs: "none", md: "block" },
        }}
      >
        <Button
          variant="plain-gray"
          startDecorator={<ArrowLeftIcon width={20} height={20} />}
          onClick={() => {
            navigate(`/client/catalogues/${catalogId}/items`);
          }}
        >
          {t("commons.backBtn")}
        </Button>
      </Box>

      {/* catalog details */}
      {catalog && (
        <Stack
          direction={"column"}
          sx={{
            gap: "24px",
            py: { xs: "16px", lg: 0 },
            width: "100%",
          }}
        >
          {/* catalog header informations */}
          <CatalogueHeader
            catalog={catalog}
            isSellerView
            withCatalogType={false}
          />

          {/* catalog metadata informations */}
          <CatalogueMetadata data={CATALOG_METADATA} editable={true} />
        </Stack>
      )}

      {/* copy link button */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          justifyContent: "space-between",
          py: { xs: "16px", md: 0 },
          gap: { xs: "16px", md: 0 },
        }}
      >
        <Stack direction={"column"} gap={0}>
          <Typography level="title-lg">
            {t("catalog.shareCatalog.copyLinkPrompt")}
          </Typography>
          <Typography level="text-md-regular">
            {t("catalog.shareCatalog.copyLinkSubPrompt")}
          </Typography>
        </Stack>

        <CopyLinkButton
          url={t("catalog.copyLinkPrompt", {
            catalogueLink: `${
              window.location.href.split("/client")[0]
            }/public/suppliers/${catalogId}/items`,
          })}
          variant={"linkz-outlined-primary"}
          isRounded={true}
        />
      </Stack>

      {/* tabs */}
      <Tabs
        aria-label="Basic tabs"
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          backgroundColor: "transparent",
          width: { xs: "90dvw", md: "100%" },
        }}
      >
        <TabList
          sticky="top"
          sx={{
            top: { xs: 60, md: 80 },
            width: "100%",
            [`&& .${tabClasses.root}`]: {
              "&:hover": {
                bgcolor: "transparent",
              },
              [`&.${tabClasses.selected}`]: {
                "&::after": {
                  bgcolor: "primary.500",
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                  height: 2,
                },
                color: "primary.plainColor",
              },
              bgcolor: "transparent",
              flex: "initial",
              paddingY: 2,
            },
          }}
        >
          <Tab
            value={"share"}
            key={"share"}
            sx={{
              fontWeight: "600",
              whiteSpace: "nowrap",
              minWidth: { xs: "50%", md: 310 },
              flex: "none",
              scrollSnapAlign: "start",
              flexDirection: "column",
            }}
          >
            {t("catalog.shareCatalog.tabs.share")}
          </Tab>
          <Tab
            value={"manage"}
            key={"manage"}
            sx={{
              fontWeight: "600",
              whiteSpace: "nowrap",
              minWidth: { xs: "50%", md: 310 },
              flex: "none",
              scrollSnapAlign: "start",
              flexDirection: "column",
            }}
          >
            {t("catalog.shareCatalog.tabs.manage")}
          </Tab>
        </TabList>

        {/* default share empty tab */}
        <TabPanel
          value={"share"}
          key={"share"}
          sx={{
            px: 0,
            py: 0,
          }}
        >
          <SharePanel catalogId={catalogId} />
        </TabPanel>

        {/* default manage empty tab */}
        <TabPanel
          value={"manage"}
          key={"manage"}
          sx={{
            px: 0,
            py: 0,
          }}
        >
          <ManagePanel catalogId={catalogId} />
        </TabPanel>
      </Tabs>
    </Sheet>
  );
};

const SharePanel = ({ catalogId }: { catalogId: string }) => {
  const { t } = useTranslation();

  const businessId = getActiveBusiness()?.id;

  const [showAlert, setShowAlert] = useState(false);
  const [accountOptions, setAccountOptions] = useState<TempAccountType[]>([]); // options for autocomplete
  const [selectedAccounts, setSelectedAccounts] = useState<TempAccountType[]>(
    []
  ); // will contains selected, trigger cancel button on accountList render
  const [recipients, setRecipients] = useState<RecipientType[]>([]); // will contains all recipient (pair of email and business)
  const [pendingNewAccount, setPendingNewAccount] = useState<any | null>(null);

  /**
   * validate email to see if already exist in list
   * @param email
   * @param emails
   * @returns
   */
  const validateEmail = (
    email: string,
    emails: string[] = selectedAccounts.map((acc) => acc.email)
  ) => {
    const valid =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return valid.test(email.toLowerCase()) && !emails.includes(email);
  };

  /**
   * validate phone number format
   * @param input
   * @param inputs
   * @returns
   */
  const validatePhoneNumber = (
    input: string,
    inputs: string[] = selectedAccounts.map((acc) => acc.phoneNumber)
  ) => {
    const phoneRegex = /^\+\d+$/;
    if (!phoneRegex.test(input)) {
      return false;
    }

    if (inputs.includes(input)) {
      return false;
    }

    const digitsOnly = input.slice(1);
    console.log("MOBILE VALIDATION", {
      digitsOnly,
      verdict: digitsOnly.length < 9 || digitsOnly.length > 14,
    });

    if (digitsOnly.length < 9 || digitsOnly.length > 14) {
      return false;
    }

    return true;
  };

  function sanitizeValue(value: string): string {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailRegex.test(value.trim())) {
      return value.trim();
    }

    const digits = value.replace(/\D/g, "");

    return `+${digits}`;
  }

  /**
   * adds account to the list
   * @param account
   */
  const handleAddEmail = (account: TempAccountType) => {
    setSelectedAccounts((prev) => [...prev, account]);
    setRecipients((prev) => [
      ...prev,
      {
        email: account.email,
        phoneNumber: account.phoneNumber,
        phoneNumberCountryCode: account.phoneNumberCountryCode,
        hasBusiness: account.role ? true : false,
        selectedBusinessId:
          account.role?.length === 1 ? account.role[0].business.id : null,
        authStrategy: account.authStrategy,
      },
    ]);
  };

  /**
   * remove account from the list
   * @param account
   */
  const handleRemoveEmail = (account: TempAccountType) => {
    const removedList = selectedAccounts.filter(
      (acc) => acc.email !== account.email
    );
    const removedRecipient = recipients.filter(
      (acc) => acc.email !== account.email
    );

    setSelectedAccounts(removedList);
    setRecipients(removedRecipient);
  };

  /**
   * select business for an account to send invitation to
   * @param email
   * @param businessId
   */
  const handleSelectBusiness = (email: string, businessId: string) => {
    const index = recipients.findIndex((rec) => rec.email === email);
    if (index !== -1) {
      setRecipients([
        ...recipients.slice(0, index),
        { ...recipients[index], selectedBusinessId: businessId },
        ...recipients.slice(index + 1),
      ]);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || !pendingNewAccount) {
      return;
    }

    const emails = selectedAccounts.map((acc) =>
      acc.authStrategy === "PHONE_AUTH" ? acc.phoneNumber : acc.email
    );
    const isExist =
      emails.includes(pendingNewAccount.email) ||
      emails.includes(pendingNewAccount.phoneNumber);

    if (event.key === "Enter" && pendingNewAccount && !isExist) {
      handleAddEmail(pendingNewAccount);
      setPendingNewAccount(null);
    }
  };

  const onSubmit = () => {
    if (!businessId) {
      return;
    }

    // validate
    for (let i = 0; i < recipients.length; i++) {
      if (recipients[i].hasBusiness) {
        if (recipients[i].selectedBusinessId === null) {
          console.error(
            `Error on item ${i + 1}, recipient business not selected`
          );

          return false;
        }
      }
    }

    const invitesPayload: ICatalogInviteCreatePayload[] = [];
    recipients.forEach((rec) => {
      if (!rec.phoneNumber && !rec.email) {
        return;
      }

      const isPhoneAuth = rec.authStrategy === "PHONE_AUTH";

      if (isPhoneAuth && (!rec.phoneNumber || !rec.phoneNumberCountryCode)) {
        return;
      }

      if (!isPhoneAuth && !rec.email) {
        return;
      }

      invitesPayload.push({
        catalog: catalogId,
        senderBusiness: businessId,
        catalogInvitationStatus: rec.hasBusiness ? "ACCEPTED" : "PENDING",
        recipientEmail: isPhoneAuth
          ? `+${rec.phoneNumberCountryCode}${rec.phoneNumber}`
          : rec.email!,
        recipientBusiness: rec.hasBusiness ? rec.selectedBusinessId : null,
      });
    });

    console.log("PHONE NUMBER COUNTRY CODE", {
      recipients,
      selectedAccounts,
      invitesPayload,
    });

    InventoryService.createCatalogInvite({
      catalogInvites: invitesPayload,
      catalogId: catalogId,
    })
      .then((res) => {
        if (res) {
          setShowAlert(true);

          setTimeout(() => {
            setShowAlert(false);
          }, 3000);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setSelectedAccounts([]);
      });
  };

  return (
    <Stack
      direction={"column"}
      sx={{ py: "16px", justifyContent: "center", gap: "16px" }}
    >
      {/* prompt lg */}
      <Typography level="text-md-regular" display={{ xs: "none", md: "block" }}>
        {t("catalog.shareCatalog.invitePrompt")}
      </Typography>

      {/* prompt xs */}
      <Typography level="text-sm-regular" display={{ xs: "block", md: "none" }}>
        {t("catalog.shareCatalog.invitePrompt")}
      </Typography>

      {/* search input */}
      <Autocomplete
        sx={{
          width: { xs: "100%", md: "30%" },
        }}
        placeholder={t("catalog.inventory.sendCatalogModalPlaceholder")}
        options={[...accountOptions]}
        // getOptionLabel={(option) =>
        //   option.authStrategy === "PHONE_AUTH"
        //     ? `+${option.phoneNumberCountryCode}${option.phoneNumber}`
        //     : option.email
        // }
        getOptionLabel={(option) =>
          `+${option.phoneNumberCountryCode}${option.phoneNumber} ${option.email}`
        }
        onInputChange={(_event: React.SyntheticEvent, value: string) => {
          if (validateEmail(value) || validatePhoneNumber(value)) {
            AccountService.getAccountByEmail(sanitizeValue(value))
              .then((res) => {
                const emails = selectedAccounts.map((acc) =>
                  acc.authStrategy === "PHONE_AUTH"
                    ? acc.phoneNumber
                    : acc.email
                );
                const newOptions = res.filter(
                  (acc) =>
                    !emails.includes(acc.email) ||
                    !emails.includes(acc.phoneNumber)
                );

                console.log("NEW OPTIONS", { newOptions });
                setAccountOptions([...newOptions]);

                if (res.length === 0) {
                  const isPhoneNumber = validatePhoneNumber(value);
                  const newAccount = {
                    email: !isPhoneNumber ? value : "",
                    phoneNumber: isPhoneNumber ? value.slice(3) : "",
                    phoneNumberCountryCode: isPhoneNumber
                      ? value.substring(1, 3)
                      : "",
                    authStrategy: isPhoneNumber
                      ? "PHONE_AUTH"
                      : "EMAIL_AND_PASSWORD",
                  };

                  setAccountOptions([newAccount]);
                  setPendingNewAccount(newAccount);
                }
              })
              .catch((error) => console.error(error));
          } else {
            console.log("INVALID");
            setPendingNewAccount(null);
            setAccountOptions([]);
          }
        }}
        onKeyDown={handleKeyDown}
        renderOption={(props, option) => (
          <AutocompleteOption
            {...props}
            sx={{
              "&:hover": {
                backgroundColor: "var(--joy-palette-neutral-100)",
              },
            }}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              flex={1}
            >
              <Stack>
                {option.authStrategy === "PHONE_AUTH"
                  ? `+${option.phoneNumberCountryCode}${option.phoneNumber}`
                  : option.email}
              </Stack>
              <Stack>
                <Add />
              </Stack>
            </Stack>
          </AutocompleteOption>
        )}
        onChange={(
          _event: React.SyntheticEvent,
          value: TempAccountType | null
        ) => {
          if (!value) {
            return;
          }

          const accountIdentifiers = selectedAccounts.filter(
            (acc) => acc.email || acc.phoneNumber !== null
          );
          const isIdentifierValid = value.email
            ? validateEmail(
                value.email,
                accountIdentifiers.map((acc) => acc.email)
              )
            : validatePhoneNumber(
                `+${value.phoneNumberCountryCode}${value.phoneNumber}`,
                selectedAccounts.map(
                  (acc) => `+${acc.phoneNumberCountryCode}${acc.phoneNumber}`
                )
              );

          if (isIdentifierValid) {
            handleAddEmail(value);
            setPendingNewAccount(null);
          }
        }}
        endDecorator={<MagnifyingGlassIcon width={20} height={20} />}
      />

      {/* account list */}
      <List
        sx={{
          overflow: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          pb: "80px",
          gap: "16px",
        }}
      >
        {selectedAccounts.map((account, index) => {
          return (
            <ListItem key={account.id + "___" + index}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{
                  pt: { xs: 0, md: "8px" },
                  pb: "8px",
                  width: "100%",
                  borderBottom: `1px solid ${theme.palette.others.list_item_border}`,
                  gap: "8px",
                }}
              >
                {/* email / phone number */}
                <Stack
                  direction={"row"}
                  sx={{
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <UserCircleIcon width={24} height={24} />

                  <Typography level="text-sm-medium">
                    {account.authStrategy === "PHONE_AUTH"
                      ? `+${account.phoneNumberCountryCode}${account.phoneNumber}`
                      : account.email}
                  </Typography>

                  {/* delete button xs */}
                  <IconButton
                    variant="plain"
                    onClick={() => {
                      handleRemoveEmail(account);
                    }}
                    sx={{
                      display: { xs: "flex", md: "none" },
                      ml: "auto",
                    }}
                  >
                    <XMarkIcon width={24} height={24} />
                  </IconButton>
                </Stack>

                {/* business select */}
                {account.role && account.role.length > 1 && (
                  <Select
                    size="sm"
                    placeholder="Select Business"
                    slotProps={{
                      button: {
                        sx: { whiteSpace: "nowrap" },
                      },
                    }}
                    sx={{
                      width: { xs: "100%", md: "400px" },
                      ml: "auto",
                    }}
                    onChange={(
                      _event: React.SyntheticEvent | null,
                      value: string | null
                    ) => {
                      if (value) {
                        handleSelectBusiness(account.email, value);
                      }
                    }}
                  >
                    {account.role.map((r) => (
                      <Option key={r.business.id} value={r.business?.id}>
                        {r?.business?.companyName}
                      </Option>
                    ))}
                  </Select>
                )}

                {/* delete button lg */}
                <IconButton
                  variant="plain"
                  onClick={() => {
                    handleRemoveEmail(account);
                  }}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    ml: { xs: "auto", md: "16px" },
                  }}
                >
                  <XMarkIcon width={24} height={24} />
                </IconButton>
              </Stack>
            </ListItem>
          );
        })}
      </List>

      {/* buttons */}
      <Stack
        direction={"row"}
        sx={{
          justifyContent: { xs: "center", md: "flex-end" },
          position: "fixed",
          width: "100%",
          maxWidth: "inherit",
          bottom: 0,
          left: 0,
          bgcolor: theme.palette.common.white,
          px: { xs: "16px", md: "24px" },
          py: "16px",
        }}
      >
        <Divider
          sx={{
            width: "100dvw",
            position: "absolute",
            display: { xs: "none", lg: "block" },
            left: "240px",
            top: 0,
            backgroundColor: theme.palette.others.disabled,
          }}
        />

        <Button
          disabled={selectedAccounts.length === 0}
          type="button"
          onClick={onSubmit}
          startDecorator={<PaperAirplaneIcon width={20} height={20} />}
          sx={{ width: { xs: "100%", md: "100px" } }}
        >
          {t("commons.inviteBtn")}
        </Button>
      </Stack>

      {/* modals */}
      <SuccessModal
        title={t("commons.successModalTitle")}
        content={t("catalog.catalogSent")}
        open={showAlert}
      />
    </Stack>
  );
};

const ManagePanel = ({ catalogId }: { catalogId: string }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState<any>();

  const { data, refetch } = useQuery({
    queryKey: ["catalog-invitations", catalogId],
    queryFn: () => InventoryService.getCatalogInvitationsData({ catalogId }),
  });

  const sendInvitationEmailMutation = useMutation({
    mutationFn: CatalogService.sendEmailInvitation,
    onSuccess: () => {
      TrackerService.track(
        TRACKER_CONSTANTS.REGISTRATION_VIA_CATALOG.events
          .SellerSendInvitationToBuyer
      );

      toast(
        showSuccessToast(t("catalog.shareCatalog.successResend")),
        SuccessToastConfig
      );
    },
    onError: (error: any) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const sendInvitationWhatsappMutation = useMutation({
    mutationFn: CatalogService.sendWhatsappInvitation,
    onSuccess: () => {
      TrackerService.track(
        TRACKER_CONSTANTS.REGISTRATION_VIA_CATALOG.events
          .SellerSendInvitationToBuyer
      );

      toast(
        showSuccessToast(t("catalog.shareCatalog.successResend")),
        SuccessToastConfig
      );
    },
    onError: (error: any) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const removeInvitationMutation = useMutation({
    mutationFn: CatalogService.removeCatalogAccess,
    onSuccess: () => {
      toast(
        showSuccessToast(t("catalog.shareCatalog.successDelete")),
        SuccessToastConfig
      );
      setShowConfirmDeleteModal(false);

      try {
        setTimeout(() => {
          console.log("SHOULD BE REFETCHING BY NOW");
          refetch();
        }, 1000);
      } catch (error) {
        console.error("Refetch failed:", error);
      }
    },
    onError: (error: any) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const handleResendInvitation = (
    accountId: string,
    mode: "email" | "whatsapp"
  ) => {
    console.log("ACCOUNTID", accountId);
    if (mode === "email") {
      sendInvitationEmailMutation.mutate({
        catalogId,
        accountIds: [accountId],
      });
    } else if (mode === "whatsapp") {
      sendInvitationWhatsappMutation.mutate({
        catalogId,
        accountIds: [accountId.replace(/ /g, "")],
      });
    }
  };

  /**
   * remove invitation based on catalog-business-access
   * @param cbaToRemove
   */
  const handleRemove = (cbaToRemove: any) => {
    removeInvitationMutation.mutate([cbaToRemove]);
  };

  if (!data) {
    return <NotFound />;
  }

  console.log("INVITE", data);

  return (
    <Stack
      direction={"column"}
      sx={{ py: "16px", justifyContent: "center", gap: "16px" }}
    >
      {/* prompt lg */}
      <Typography level="text-md-regular" display={{ xs: "none", md: "block" }}>
        {t("catalog.shareCatalog.managePrompt")}
      </Typography>

      {/* prompt xs */}
      <Typography level="text-sm-regular" display={{ xs: "block", md: "none" }}>
        {t("catalog.shareCatalog.managePrompt")}
      </Typography>

      {/* invitation list */}
      {data.catalogInvitations.length > 0 ? (
        <List
          sx={{
            overflow: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            pb: "80px",
            gap: "8px",
          }}
        >
          {data.catalogInvitations.map((invite: any, index: number) => {
            const selectedBusiness = invite.account.businesses?.find(
              (business: any) => business.id === invite.recipientBusiness
            );

            return (
              <ListItem key={invite.id + "___" + index}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  sx={{
                    py: "8px",
                    width: "100%",
                    borderBottom: `1px solid ${theme.palette.others.list_item_border}`,
                    gap: "8px",
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  {/* account details */}
                  <Stack
                    direction={"row"}
                    sx={{
                      gap: "8px",
                      width: { xs: "100%", md: "60%" },
                    }}
                  >
                    {/* avatar */}
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        border: "1px solid",
                        borderColor: theme.palette.others.disabled,
                        fontWeight: "bold",
                        alignItems: "center",
                        justifyContent: "center",
                        width: { xs: 32, md: 40 },
                        height: { xs: 32, md: 40 },
                        fontSize: "sm",
                        color: theme.palette.primary[500],
                        bgcolor: theme.palette.primary[50],
                        borderRadius: "50%",
                      }}
                    >
                      {invite.account.id ? (
                        getInitials(
                          invite.account?.profile?.fullName ??
                            invite.account.email ??
                            ""
                        )
                      ) : invite.account.authStrategy === "PHONE_AUTH" ? (
                        <PhoneIcon width={23.25} height={23.2} />
                      ) : (
                        <EnvelopeIcon width={23.25} height={23.2} />
                      )}
                    </Box>

                    <Stack direction={"column"}>
                      <Typography level="text-sm-bold">
                        {invite.account.id
                          ? invite.account.profile.fullName
                          : invite.account.authStrategy === "PHONE_AUTH"
                          ? invite.account.phoneNumber
                          : invite.account.email}
                      </Typography>

                      <Typography level="text-xs-regular">
                        {invite.account.id
                          ? selectedBusiness?.companyName
                          : t("commons.error.notRegisteredOnLinkzYet")}
                      </Typography>

                      {/* last updated xs */}
                      <Stack
                        direction={"row"}
                        sx={{
                          display: { xs: "flex", md: "none" },
                        }}
                      >
                        <Typography level="text-xs-medium">
                          {t("commons.lastUpdated")}:
                        </Typography>

                        <Typography level="text-xs-semibold">
                          {" "}
                          {formatDate(new Date(invite.updatedAt))}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>

                  {/* last updated lg */}
                  <Stack
                    direction={"row"}
                    sx={{
                      display: { xs: "none", md: "flex" },
                    }}
                  >
                    <Typography level="text-xs-medium">
                      {t("commons.lastUpdated")}:
                    </Typography>

                    <Typography level="text-xs-semibold">
                      {" "}
                      {formatDate(new Date(invite.updatedAt))}
                    </Typography>
                  </Stack>

                  {/* buttons */}
                  <Stack
                    direction={"row"}
                    sx={{
                      gap: "8px",
                      justifyContent: "flex-end",
                      ml: "auto",
                    }}
                  >
                    {/* resend pin / invite by email button */}
                    {invite.account.authStrategy !== "PHONE_AUTH" && (
                      <Button
                        variant="linkz-outlined-primary"
                        onClick={() => {
                          handleResendInvitation(
                            invite.account.id ?? invite.account.email,
                            "email"
                          );
                        }}
                        startDecorator={
                          <PaperAirplaneIcon width={20} height={20} />
                        }
                      >
                        {t("commons.form.email")}
                      </Button>
                    )}

                    {/* resend pin / invite by whatsapp button */}
                    {/* //TODO: refactor this ridiculous conditions */}
                    <Button
                      variant="linkz-outlined-primary"
                      onClick={() => {
                        handleResendInvitation(
                          invite.account.authStrategy === "PHONE_AUTH"
                            ? invite.account.id
                              ? `+${invite.account.phoneNumberCountryCode}${invite.account.phoneNumber}`
                              : invite.account.phoneNumber
                            : `+${invite.account.profile.countryCode}${invite.account.profile.localNumber}`,
                          "whatsapp"
                        );
                      }}
                      startDecorator={
                        <PaperAirplaneIcon width={20} height={20} />
                      }
                    >
                      {t("commons.form.whatsapp")}
                    </Button>

                    {/* delete invite / access button */}
                    <Button
                      variant="linkz-outlined-danger"
                      sx={{ minWidth: "40px" }}
                      onClick={() => {
                        setInvitationToDelete(invite);
                        setShowConfirmDeleteModal(true);
                      }}
                    >
                      <TrashIcon width={20} height={20} />
                    </Button>
                  </Stack>
                </Stack>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Stack
          direction={"column"}
          sx={{
            gap: "8px",
            px: { xs: "16px", md: "24px" },
            pt: "40px",
            pb: "80px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography level="text-xl2-semibold">
            {t("catalog.shareCatalog.manageEmptyPrompt")}
          </Typography>

          <Typography level="text-sm-regular">
            {t("catalog.shareCatalog.manageEmptySubPrompt")}
          </Typography>
        </Stack>
      )}

      {/* buttons */}
      <Stack
        direction={"row"}
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: { xs: "center", md: "flex-end" },
          position: "fixed",
          width: "100%",
          maxWidth: "inherit",
          bottom: 0,
          left: 0,
          bgcolor: theme.palette.common.white,
          px: { xs: "16px", md: "24px" },
          py: "16px",
        }}
      >
        <Divider
          sx={{
            width: "100dvw",
            position: "absolute",
            display: { xs: "none", lg: "block" },
            left: "240px",
            top: 0,
            backgroundColor: theme.palette.others.disabled,
          }}
        />

        <Button
          type="button"
          onClick={() => {
            navigate(`/client/catalogues/${catalogId}/items`);
          }}
          sx={{ width: { xs: "100%", md: "55px" } }}
        >
          {t("commons.backBtn")}
        </Button>
      </Stack>

      {/* modals */}
      {invitationToDelete && (
        <ConfirmRemoveInvitationModal
          show={showConfirmDeleteModal}
          catalogInvitations={invitationToDelete}
          onClick={() =>
            handleRemove({
              businessId: invitationToDelete.recipientBusiness,
              catalogId: catalogId,
              accountId: invitationToDelete.account.id,
              // email:
              //   invitationToDelete.account.email ??
              //   invitationToDelete.account.phoneNumber,
              email: invitationToDelete.recipientEmail,
            })
          }
          onClose={() => setShowConfirmDeleteModal(false)}
        />
      )}
    </Stack>
  );
};

export default ShareCatalogue;
